import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../lib/api";
import { notify } from "../lib/notify";
import { useAttraction } from "../lib/attraction";
import { formatSlotTime } from "../lib/timeFormat";
import DashboardLayout from "../components/DashboardLayout";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import { Send, ExternalLink, ArrowLeftRight, Plus, Minus, X, Ban, Scissors, RefreshCw, Link2, Copy, Clock, XCircle, UserRoundCog, Pencil, Check, ArrowUpCircle, Mail } from "lucide-react";
import { confirmDialog } from "../components/ConfirmDialog";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? iso + "T12:00:00" : iso);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
}

function fmtClock(iso, attraction) {
  if (!iso || iso.length <= 10) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const mode12 = (attraction?.time_format || "12hr") === "12hr";
  const t = d.toLocaleTimeString("en-GB", {
    hour: mode12 ? "numeric" : "2-digit", minute: "2-digit", hour12: mode12,
    timeZone: attraction?.timezone || "Europe/London",
  });
  return mode12 ? t.replace(/\s?(am|pm)/i, (m) => m.trim().toLowerCase()) : t;
}

function fmtShort(ds) {
  if (!ds) return "—";
  const d = new Date(ds + "T12:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const { attraction } = useAttraction() || {};
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [cancelTicketsOpen, setCancelTicketsOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [editNameId, setEditNameId] = useState(null);
  const [editName, setEditName] = useState("");
  const [moveTicketsOpen, setMoveTicketsOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [links, setLinks] = useState([]);

  const load = async () => {
    try { const r = await api.get(`/orders/${id}`); setData(r.data); }
    catch (e) { setErr("Order not found"); }
  };
  const loadLinks = async () => {
    try { const r = await api.get(`/orders/${id}/payment-links`); setLinks(r.data.payment_links || []); }
    catch (e) { /* noop */ }
  };
  useEffect(() => { load(); loadLinks(); /* eslint-disable-line */ }, [id]);
  useEffect(() => {
    if (!data?.order?.event_id) return;
    api.get(`/events/${data.order.event_id}`).then((r) => setEventData(r.data)).catch(() => {});
  }, [data?.order?.event_id]);

  const saveEdit = async (patch) => {
    try { await api.patch(`/orders/${id}`, patch); notify.success("Saved"); load(); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };
  const resend = async () => {
    try { await api.post(`/orders/${id}/resend`); notify.success("Tickets resent"); }
    catch (e) { notify.error("Resend failed"); }
  };
  const copyTicketsLink = () => {
    const url = `${window.location.origin}/order/${id}/tickets`;
    navigator.clipboard?.writeText(url);
    notify.success("Tickets link copied");
  };
  const copyOneTicketLink = (attendeeId) => {
    navigator.clipboard?.writeText(`${window.location.origin}/ticket/${attendeeId}`);
    notify.success("Single-ticket link copied");
  };
  const undoCheckin = async (attendeeId, name) => {
    if (!window.confirm(`Undo check-in for ${name}? They'll show as not yet arrived and can scan in again.`)) return;
    try {
      await api.post("/checkin/undo", { attendee_id: attendeeId, device: "Dashboard" });
      notify.success("Check-in undone");
      load();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Could not undo check-in"); }
  };
  const saveName = async (attendeeId) => {    const name = editName.trim();
    if (!name) return;
    try {
      await api.patch(`/attendees/${attendeeId}/name`, { name });
      setEditNameId(null);
      notify.success("Name updated");
      load();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Could not update name"); }
  };
  const sendReminder = async () => {
    const ok = await confirmDialog({
      title: "Send the day-before reminder?",
      description: `${data.order.customer_email} gets your "Day-before reminder" email now, even if they've already had it.`,
      confirmLabel: "Send it now",
    });
    if (!ok) return;
    try { await api.post(`/orders/${id}/send-reminder`); notify.success("Reminder sent"); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Could not send the reminder"); }
  };
  const cancelOrder = async () => {
    const ok = await confirmDialog({
      title: "Cancel entire order?",
      description: "All tickets will be cancelled and the full amount refunded to the customer's card. This cannot be undone.",
      confirmLabel: "Yes, cancel & refund", destructive: true,
    });
    if (!ok) return;
    try { const r = await api.post(`/orders/${id}/cancel`); notify.success(`Order cancelled · ${poundsFromPence(r.data.refunded)} refunded`); load(); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Cancel failed"); }
  };

  if (err) return <DashboardLayout title="Order"><div className="text-[#fca5a5]">{err}</div></DashboardLayout>;
  if (!data) return <DashboardLayout title="Loading…" />;
  const { order, attendees, event_date, time_slot } = data;

  const priceMap = {};
  (order.line_items || []).forEach((li) => { priceMap[li.ticket_type_id] = li.unit_price_pence; });
  const priceFor = (a) => a.unit_price_pence ?? priceMap[a.ticket_type_id] ?? 0;
  const activeAttendees = attendees.filter((a) => a.status !== "cancelled");
  const refunded = order.refunded_amount_pence || 0;
  const refundable = order.gross_amount - refunded;
  const closed = ["cancelled", "refunded"].includes(order.status);

  return (
    <DashboardLayout
      title={<>Order <span className="st-mono text-[#7a7a7a]">{order.order_number}</span></>}
      subtitle={`${event_date?.date}${time_slot ? " · " + formatSlotTime(time_slot.start_time, time_slot.end_time, attraction) : ""}`}
      actions={
        <div className="flex gap-2 items-center">
          <OrderStatusBadge status={order.status} />
          <button className="st-btn st-btn-ghost" onClick={copyTicketsLink} data-testid="copy-tickets-link-btn"><Link2 size={14} /> Copy tickets link</button>
          <button className="st-btn st-btn-ghost" onClick={resend} data-testid="resend-btn"><Send size={14} /> Resend</button>
        </div>
      }
    >
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left — customer + attendees */}
        <div className="lg:col-span-2 space-y-4">
          <div className="st-card p-4">
            <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <Info label="Customer" value={`${order.customer_first_name || ""} ${order.customer_last_name || ""}`.trim() || order.customer_name} />
              <Info label="Email" value={order.customer_email} />
              <Info label="Phone" value={order.customer_phone || "—"} />
              <Info label="Postcode" value={order.postcode || "—"} />
              <Info label="Booked on" value={fmtDate(order.created_at)} sub={fmtClock(order.created_at, attraction)} testid="booked-on" />
              <Info label="Visit date" value={fmtDate(event_date?.date)} />
              {time_slot && <Info label="Time slot" value={formatSlotTime(time_slot.start_time, time_slot.end_time, attraction)} />}
            </div>
          </div>

          <div className="st-card p-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Attendees ({activeAttendees.length} active)</h3>
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a]">
                <tr><th className="py-2">Name</th><th>Ticket</th><th>Price</th><th>Date</th><th>Check-in</th><th>Ticket</th></tr>
              </thead>
              <tbody data-testid="attendees-tbody">
                {attendees.map((a) => {
                  const cancelled = a.status === "cancelled";
                  const moved = a.event_date && event_date?.date && a.event_date !== event_date.date;
                  return (
                    <tr key={a.id} className={`border-t border-[#1a1a1a] ${cancelled ? "opacity-45 line-through" : ""}`}>
                      <td className="py-2.5">
                        {editNameId === a.id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <input autoFocus className="st-input py-1 text-sm w-40" value={editName}
                                   onChange={(e) => setEditName(e.target.value)}
                                   onKeyDown={(e) => { if (e.key === "Enter") saveName(a.id); if (e.key === "Escape") setEditNameId(null); }}
                                   data-testid={`attendee-name-input-${a.id}`} />
                            <button className="text-[#86efac] hover:text-white" title="Save" onClick={() => saveName(a.id)} data-testid={`attendee-name-save-${a.id}`}><Check size={14} /></button>
                            <button className="text-[#8a8a8a] hover:text-white" title="Cancel" onClick={() => setEditNameId(null)} data-testid={`attendee-name-cancel-${a.id}`}><X size={14} /></button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 group">
                            {a.name}
                            {!cancelled && (
                              <button className="text-[#5a5a5a] hover:text-white" title="Change the name on this ticket"
                                      onClick={() => { setEditNameId(a.id); setEditName(a.name || ""); }}
                                      data-testid={`attendee-name-edit-${a.id}`}><Pencil size={12} /></button>
                            )}
                          </span>
                        )}
                      </td>
                      <td>{a.ticket_type_name}</td>
                      <td>{poundsFromPence(priceFor(a))}</td>
                      <td>
                        {moved ? (
                          <span className="st-chip st-chip-amber whitespace-nowrap" data-testid={`attendee-moved-${a.id}`}>
                            Moved · {fmtShort(a.event_date)}{a.slot_start_time ? ` · ${a.slot_start_time}` : ""}
                          </span>
                        ) : a.pending_move ? (
                          <span className="st-chip whitespace-nowrap" data-testid={`attendee-pending-move-${a.id}`}>Move pending payment</span>
                        ) : a.pending_upgrade ? (
                          <span className="st-chip st-chip-amber whitespace-nowrap" data-testid={`attendee-pending-upgrade-${a.id}`}>
                            Upgrade to {a.pending_upgrade.ticket_type_name} pending payment
                          </span>
                        ) : (
                          <span className="text-xs text-[#7a7a7a]">Same as order</span>
                        )}
                      </td>
                      <td>{cancelled ? <span className="st-chip">Cancelled</span> : a.checked_in ? (
                        <button className="st-chip st-chip-green hover:opacity-80" title="Undo this check-in"
                                onClick={() => undoCheckin(a.id, a.name)} data-testid={`undo-checkin-${a.id}`}>
                          In · {a.checked_in_at?.slice(11, 16)} <X size={10} className="inline -mt-0.5 ml-0.5" />
                        </button>
                      ) : <span className="st-chip">Not yet</span>}</td>
                      <td>
                        {!cancelled && (
                          <span className="inline-flex items-center gap-2">
                            <a className="st-link inline-flex items-center gap-1" href={`/ticket/${a.id}`} target="_blank" rel="noreferrer" data-testid={`ticket-link-${a.id}`}>Open <ExternalLink size={12} /></a>
                            <button className="text-[#8a8a8a] hover:text-white" title="Copy this ticket's link"
                                    onClick={() => copyOneTicketLink(a.id)} data-testid={`copy-ticket-${a.id}`}><Copy size={12} /></button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="st-card p-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Customer details</h3>
            <CustomerEdit order={order} onSave={saveEdit} />
          </div>
        </div>

        {/* Right — summary + actions */}
        <div className="space-y-4 h-fit">
          <div className="st-card p-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Summary</h3>
            {(order.line_items || []).map((li, i) => (
              <div key={i} className="flex justify-between text-sm py-1.5">
                <span>{li.quantity}× {li.name}</span><span>{poundsFromPence(li.unit_price_pence * li.quantity)}</span>
              </div>
            ))}
            {(order.addon_items || []).map((a, i) => (
              <div key={i} className="flex justify-between text-sm py-1.5 text-[#dadada]">
                <span>{a.quantity}× {a.name}</span><span>{poundsFromPence(a.unit_price_pence * a.quantity)}</span>
              </div>
            ))}
            <div className="st-divider my-3" />
            <div className="flex justify-between font-semibold"><span>Total</span><span>{poundsFromPence(order.gross_amount)}</span></div>
            <div className="flex justify-between text-xs text-[#8a8a8a] mt-1"><span>Commission</span><span>{poundsFromPence(order.commission_amount)}</span></div>
            {refunded > 0 && <div className="flex justify-between text-xs text-[#e8c987] mt-1" data-testid="summary-refunded"><span>Refunded</span><span>−{poundsFromPence(refunded)}</span></div>}
          </div>

          <div className="st-card p-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Manage order</h3>
            {closed ? (
              <div className="text-sm text-[#8a8a8a]" data-testid="order-closed-note">This order is {order.status.replace("_", " ")}. No further actions available.</div>
            ) : (
              <div className="space-y-2">
                <button className="st-btn st-btn-ghost w-full justify-start text-sm" onClick={() => setCancelTicketsOpen(true)} disabled={activeAttendees.length === 0} data-testid="cancel-tickets-btn">
                  <Scissors size={14} /> Cancel selected tickets
                </button>
                <button className="st-btn st-btn-ghost w-full justify-start text-sm" onClick={() => setRefundOpen(true)} disabled={refundable <= 0} data-testid="partial-refund-btn">
                  <RefreshCw size={14} /> Issue partial refund
                </button>
                <button className="st-btn st-btn-ghost w-full justify-start text-sm text-[#fca5a5]" onClick={cancelOrder} data-testid="cancel-order-btn">
                  <Ban size={14} /> Cancel entire order
                </button>
                <div className="st-divider my-2" />
                <button className="st-btn st-btn-ghost w-full justify-start text-sm" onClick={() => setMoveOpen(true)} data-testid="move-booking-btn">
                  <ArrowLeftRight size={14} /> Move booking
                </button>
                <button className="st-btn st-btn-ghost w-full justify-start text-sm" onClick={() => setMoveTicketsOpen(true)} disabled={activeAttendees.length === 0} data-testid="move-tickets-btn">
                  <UserRoundCog size={14} /> Move individual tickets
                </button>
                <button className="st-btn st-btn-ghost w-full justify-start text-sm" onClick={() => setUpgradeOpen(true)} disabled={activeAttendees.length === 0} data-testid="upgrade-tickets-btn">
                  <ArrowUpCircle size={14} /> Upgrade ticket type
                </button>
                <button className="st-btn st-btn-ghost w-full justify-start text-sm" onClick={() => setAddOpen(true)} data-testid="add-tickets-btn">
                  <Plus size={14} /> Add tickets
                </button>
                <div className="st-divider my-2" />
                <button className="st-btn st-btn-ghost w-full justify-start text-sm" onClick={sendReminder} data-testid="send-reminder-btn">
                  <Mail size={14} /> Send day-before reminder
                </button>
              </div>
            )}
          </div>

          {links.length > 0 && (
            <div className="st-card p-4" data-testid="pending-payments">
              <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Payment links</h3>
              <div className="space-y-2">
                {links.map((l) => <PaymentLinkRow key={l.id} link={l} onChange={() => { loadLinks(); load(); }} />)}
              </div>
            </div>
          )}
        </div>
      </div>

      {cancelTicketsOpen && (
        <CancelTicketsModal orderId={id} attendees={activeAttendees} priceFor={priceFor}
          onClose={() => setCancelTicketsOpen(false)}
          onDone={(r) => { setCancelTicketsOpen(false); notify.success(`${r.cancelled} ticket(s) cancelled · ${poundsFromPence(r.refunded)} refunded`); load(); }} />
      )}
      {refundOpen && (
        <PartialRefundModal orderId={id} maxPence={refundable}
          onClose={() => setRefundOpen(false)}
          onDone={(r) => { setRefundOpen(false); notify.success(`${poundsFromPence(r.refunded)} refunded`); load(); }} />
      )}
      {moveOpen && eventData && (
        <MoveBookingModal orderId={id} order={order} attendees={activeAttendees} eventData={eventData} attraction={attraction}
          onClose={() => setMoveOpen(false)}
          onDone={(msg) => { setMoveOpen(false); notify.success(msg); load(); loadLinks(); }} />
      )}
      {moveTicketsOpen && eventData && (
        <MoveTicketsModal orderId={id} order={order} attendees={activeAttendees} eventData={eventData} attraction={attraction}
          onClose={() => setMoveTicketsOpen(false)}
          onDone={(msg) => { setMoveTicketsOpen(false); notify.success(msg); load(); loadLinks(); }} />
      )}
      {upgradeOpen && eventData && (
        <UpgradeTicketsModal orderId={id} order={order} attendees={activeAttendees} eventData={eventData}
          onClose={() => setUpgradeOpen(false)}
          onDone={(msg) => { setUpgradeOpen(false); notify.success(msg); load(); loadLinks(); }} />
      )}
      {addOpen && eventData && (
        <AddTicketsModal orderId={id} order={order} eventData={eventData}
          onClose={() => setAddOpen(false)}
          onDone={(msg) => { setAddOpen(false); notify.success(msg); load(); loadLinks(); }} />
      )}
    </DashboardLayout>
  );
}

function Info({ label, value, sub, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">{label}</div>
      <div className="mt-0.5">{value}</div>
      {sub && <div className="text-xs text-[#8a8a8a] mt-0.5" data-testid={testid ? `${testid}-time` : undefined}>at {sub}</div>}
    </div>
  );
}

function CancelTicketsModal({ orderId, attendees, priceFor, onClose, onDone }) {
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ids = Object.keys(selected).filter((k) => selected[k]);
  const total = attendees.filter((a) => selected[a.id]).reduce((s, a) => s + priceFor(a), 0);

  const submit = async () => {
    setBusy(true); setErr("");
    try { const r = await api.post(`/orders/${orderId}/cancel-tickets`, { attendee_ids: ids }); onDone(r.data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Cancel failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 st-fade-up" data-testid="cancel-tickets-modal">
      <div className="st-card w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Cancel selected tickets</h2>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-sm text-[#bbb] mb-3">Choose which tickets to cancel. The selected amount will be refunded to the customer's card.</p>
        <div className="space-y-2 max-h-[280px] overflow-y-auto">
          {attendees.map((a) => (
            <label key={a.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-[#1a1a1a] hover:bg-[#0e0e0e] cursor-pointer" data-testid={`cancel-att-${a.id}`}>
              <span className="flex items-center gap-2">
                <input type="checkbox" checked={!!selected[a.id]} onChange={(e) => setSelected({ ...selected, [a.id]: e.target.checked })} />
                <span>{a.name} <span className="text-xs text-[#8a8a8a]">· {a.ticket_type_name}</span></span>
              </span>
              <span className="text-sm font-semibold">{poundsFromPence(priceFor(a))}</span>
            </label>
          ))}
        </div>
        {err && <div className="text-sm text-[#fca5a5] mt-3">{err}</div>}
        <div className="flex items-center justify-between mt-5">
          <div className="text-sm">Refund: <span className="font-bold text-[#e8c987]">{poundsFromPence(total)}</span></div>
          <div className="flex gap-2">
            <button className="st-btn st-btn-ghost" onClick={onClose} disabled={busy}>Close</button>
            <button className="st-btn" onClick={submit} disabled={busy || ids.length === 0} data-testid="confirm-cancel-tickets">{busy ? "Cancelling…" : `Cancel ${ids.length} & refund`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PartialRefundModal({ orderId, maxPence, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const max = (maxPence / 100).toFixed(2);

  const submit = async () => {
    const pence = Math.round(parseFloat(amount || "0") * 100);
    if (!pence || pence <= 0) { setErr("Enter an amount"); return; }
    if (pence > maxPence) { setErr(`Maximum refundable is £${max}`); return; }
    setBusy(true); setErr("");
    try { const r = await api.post(`/orders/${orderId}/refund/partial`, { amount_pence: pence, reason }); onDone(r.data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Refund failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 st-fade-up" data-testid="partial-refund-modal">
      <div className="st-card w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Partial refund</h2>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-white"><X size={18} /></button>
        </div>
        <label className="block">
          <span className="text-xs text-[#8a8a8a]">Amount (max £{max})</span>
          <div className="relative mt-1">
            <span className="absolute left-3 top-2.5 text-[#7a7a7a]">£</span>
            <input type="number" step="0.01" min="0" max={max} className="st-input pl-7" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" />
          </div>
        </label>
        <label className="block mt-3">
          <span className="text-xs text-[#8a8a8a]">Reason (optional)</span>
          <input className="st-input mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. goodwill gesture" data-testid="refund-reason" />
        </label>
        {err && <div className="text-sm text-[#fca5a5] mt-3">{err}</div>}
        <div className="flex justify-end gap-2 mt-5">
          <button className="st-btn st-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="st-btn" onClick={submit} disabled={busy} data-testid="confirm-partial-refund">{busy ? "Refunding…" : "Issue refund"}</button>
        </div>
      </div>
    </div>
  );
}

function CustomerEdit({ order, onSave }) {
  const [name, setName] = useState(order.customer_name);
  const [email, setEmail] = useState(order.customer_email);
  const [phone, setPhone] = useState(order.customer_phone || "");
  return (
    <div className="grid md:grid-cols-3 gap-3">
      <div>
        <label className="text-xs text-[#8a8a8a]">Name</label>
        <input className="st-input mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="cust-name" />
      </div>
      <div>
        <label className="text-xs text-[#8a8a8a]">Email</label>
        <input className="st-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="cust-email" />
      </div>
      <div>
        <label className="text-xs text-[#8a8a8a]">Phone</label>
        <input className="st-input mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="cust-phone" />
      </div>
      <div className="md:col-span-3">
        <button className="st-btn st-btn-ghost text-xs" data-testid="save-customer"
                onClick={() => onSave({ customer_name: name, customer_email: email, customer_phone: phone })}>
          Save customer details
        </button>
      </div>
    </div>
  );
}

function Counter({ value, onMinus, onPlus, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <button className="w-7 h-7 rounded-full border border-[#2b2b2b] flex items-center justify-center hover:border-[#dc2626]" onClick={onMinus} aria-label="minus"><Minus size={13} /></button>
      <span className="w-5 text-center font-bold">{value}</span>
      <button className="w-7 h-7 rounded-full border border-[#2b2b2b] flex items-center justify-center hover:border-[#dc2626]" onClick={onPlus} aria-label="plus"><Plus size={13} /></button>
    </div>
  );
}

const LINK_STATUS = {
  pending: { label: "Pending", cls: "text-[#facc15]" },
  paid: { label: "Paid", cls: "text-[#86efac]" },
  expired: { label: "Expired", cls: "text-[#fca5a5]" },
  cancelled: { label: "Cancelled", cls: "text-[#8a8a8a]" },
};

function PaymentLinkRow({ link, onChange }) {
  const [busy, setBusy] = useState(false);
  const s = LINK_STATUS[link.status] || { label: link.status, cls: "text-[#bbb]" };
  const copy = () => { navigator.clipboard?.writeText(link.payment_url); notify.success("Link copied"); };
  const resend = async () => {
    setBusy(true);
    try { await api.post(`/payment-links/${link.id}/resend`); notify.success("Link resent"); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Resend failed"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    const ok = await confirmDialog({ title: "Cancel payment link?", description: "The link will stop working and any held capacity is released.", confirmLabel: "Cancel link", destructive: true });
    if (!ok) return;
    setBusy(true);
    try { await api.post(`/payment-links/${link.id}/cancel`); notify.success("Link cancelled"); onChange(); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Cancel failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-[#1a1a1a] p-3 text-sm" data-testid={`payment-link-${link.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium truncate">{link.description}</span>
        <span className={`text-[10px] uppercase tracking-wider ${s.cls}`}>{s.label}</span>
      </div>
      <div className="flex items-center justify-between mt-1 text-xs text-[#8a8a8a]">
        <span>{poundsFromPence(link.amount_pence)} · expires {String(link.expires_at).slice(0, 10)}</span>
      </div>
      {link.status === "pending" && (
        <div className="flex gap-2 mt-2">
          <button className="st-btn st-btn-ghost text-xs py-1" onClick={copy} data-testid={`link-copy-${link.id}`}><Copy size={12} /> Copy</button>
          <button className="st-btn st-btn-ghost text-xs py-1" onClick={resend} disabled={busy} data-testid={`link-resend-${link.id}`}><Send size={12} /> Resend</button>
          <button className="st-btn st-btn-ghost text-xs py-1 text-[#fca5a5]" onClick={cancel} disabled={busy} data-testid={`link-cancel-${link.id}`}><XCircle size={12} /> Cancel</button>
        </div>
      )}
    </div>
  );
}

function ModalShell({ title, onClose, children, testid, maxW = "max-w-md" }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 st-fade-up" data-testid={testid}>
      <div className={`st-card w-full ${maxW} p-6 max-h-[88vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-white"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function demandByType(atts) {
  const d = {};
  atts.forEach((a) => { d[a.ticket_type_id] = (d[a.ticket_type_id] || 0) + 1; });
  return d;
}

/* Remaining space on a slot for ONLY the ticket types being moved — the limit checkout
   enforces. A sold-out standard slot with fast track left must not read "10 left". */
function slotFitFor(slot, demand) {
  const rows = slot.type_availability || [];
  const parts = [];
  let ok = true;
  let known = false;
  Object.entries(demand).forEach(([ttId, need]) => {
    const row = rows.find((x) => x.id === ttId);
    if (!row) return;
    known = true;
    if (row.disabled) { ok = false; parts.push(`${row.name}: n/a`); return; }
    if (row.available == null) { parts.push(`${row.name}: no limit`); return; }
    if (need > row.available) ok = false;
    parts.push(`${row.name}: ${row.available} left`);
  });
  if (!known) return { ok: true, label: slot.available == null ? "No limit" : `${slot.available} left` };
  return { ok, label: parts.join(" · ") || "No limit" };
}

function ShortfallNote({ preview }) {
  if (preview.shortfalls?.length) {
    return (
      <>
        {preview.shortfalls.map((s, i) => (
          <div key={i}>
            {s.disabled
              ? `${s.ticket_type} isn't available on that time slot.`
              : `Only ${s.available} ${s.ticket_type} left there for ${s.count} ticket(s).`}
          </div>
        ))}
      </>
    );
  }
  return <div>Only {preview.available ?? 0} spaces for {preview.ticket_count} ticket(s).</div>;
}

function MoveBookingModal({ orderId, order, attendees, eventData, attraction, onClose, onDone }) {
  const slotted = !!eventData.event?.is_time_slotted;
  const dates = (eventData.dates || []).filter((d) => !d.is_blocked && d.status !== "cancelled").sort((a, b) => a.date.localeCompare(b.date));
  const [step, setStep] = useState(1);
  const [destDate, setDestDate] = useState(null);
  const [destSlot, setDestSlot] = useState(null);
  const [perf, setPerf] = useState(null);
  const [preview, setPreview] = useState(null);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const origin = window.location.origin;

  const pickDate = async (d) => {
    setDestDate(d); setDestSlot(null); setErr("");
    if (slotted) {
      setPerf(null); setStep(2);
      try { const r = await api.get(`/events/${order.event_id}/dates/${d.id}/performance`); setPerf(r.data); }
      catch { /* noop */ }
    } else {
      await runPreview(d, null);
    }
  };
  const pickSlot = async (s) => { setDestSlot(s); await runPreview(destDate, s); };

  const runPreview = async (d, s) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/orders/${orderId}/move`, { event_date_id: d.id, time_slot_id: s?.id || null });
      setPreview(r.data); setStep(3);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Could not check availability"); }
    finally { setBusy(false); }
  };

  const submit = async (price_action) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/orders/${orderId}/move`, {
        event_date_id: destDate.id, time_slot_id: destSlot?.id || null,
        price_action, override: override || preview.sufficient, origin,
      });
      if (r.data.requires_payment) onDone("Swap pending — payment link sent to customer");
      else onDone(r.data.refunded > 0 ? `Booking moved · ${poundsFromPence(r.data.refunded)} refunded` : "Booking moved");
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Move failed"); setBusy(false); }
  };

  const isCurrent = (d, s) => order.event_date_id === d.id && (!slotted || order.time_slot_id === s?.id);

  return (
    <ModalShell title="Move booking" onClose={onClose} testid="move-modal" maxW="max-w-lg">
      {step === 1 && (
        <div data-testid="move-step-date">
          <p className="text-sm text-[#bbb] mb-3">Select a new date for this booking.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {dates.map((d) => (
              <button key={d.id} onClick={() => pickDate(d)} disabled={d.id === order.event_date_id && !slotted}
                className={`p-3 rounded-lg border text-sm text-left ${d.id === order.event_date_id ? "border-[#dc2626] bg-[#160a0a]" : "border-[#222] hover:border-[#444]"}`}
                data-testid={`move-date-${d.id}`}>
                <div className="font-medium">{fmtDate(d.date)}</div>
                {d.id === order.event_date_id && <div className="text-[10px] uppercase tracking-wider text-[#dc2626] mt-0.5">Current</div>}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="move-step-slot">
          <button className="text-xs text-[#8a8a8a] underline mb-3" onClick={() => setStep(1)}>← Back to dates</button>
          <p className="text-sm text-[#bbb] mb-3">Select a time slot on {fmtDate(destDate.date)}.</p>
          {!perf ? <div className="text-sm text-[#8a8a8a]">Loading slots…</div> : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {perf.time_slots.filter((s) => !s.is_paused).map((s) => {
                const fit = slotFitFor(s, demandByType(attendees));
                return (
                <button key={s.id} onClick={() => pickSlot(s)}
                  className={`w-full flex items-center justify-between p-2.5 rounded-lg border text-sm ${isCurrent(destDate, s) ? "border-[#dc2626]" : "border-[#222] hover:border-[#444]"}`}
                  data-testid={`move-slot-${s.id}`}>
                  <span>{formatSlotTime(s.start_time, s.end_time, attraction)}{isCurrent(destDate, s) && <span className="text-[10px] uppercase tracking-wider text-[#dc2626] ml-2">Current</span>}</span>
                  <span className={`text-xs ${fit.ok ? "text-[#8a8a8a]" : "text-[#e8c987]"}`} data-testid={`move-slot-avail-${s.id}`}>{fit.label}</span>
                </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {step === 3 && preview && (
        <div data-testid="move-step-review">
          <button className="text-xs text-[#8a8a8a] underline mb-3" onClick={() => setStep(slotted ? 2 : 1)}>← Back</button>
          <div className="st-card bg-[#0e0e0e] p-3 text-sm mb-3">
            <div className="flex justify-between"><span className="text-[#8a8a8a]">New date</span><span>{fmtDate(destDate.date)}</span></div>
            {destSlot && <div className="flex justify-between mt-1"><span className="text-[#8a8a8a]">New slot</span><span>{formatSlotTime(destSlot.start_time, destSlot.end_time, attraction)}</span></div>}
            <div className="flex justify-between mt-1"><span className="text-[#8a8a8a]">Tickets</span><span>{preview.ticket_count}</span></div>
          </div>

          {!preview.sufficient && (
            <div className="rounded-lg border border-[#3a2a0a] bg-[#1a1205] p-3 text-sm text-[#e8c987] mb-3" data-testid="move-capacity-warn">
              <ShortfallNote preview={preview} />
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="move-override" />
                <span>Override and move anyway</span>
              </label>
            </div>
          )}

          {preview.scenario === "same" && (
            <>
              <p className="text-sm mb-3">No price difference. Move this booking?</p>
              <button className="st-btn w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("none")} data-testid="move-confirm-same">{busy ? "Moving…" : "Move booking"}</button>
            </>
          )}
          {preview.scenario === "increase" && (
            <>
              <p className="text-sm mb-3">The new date costs <b className="text-[#e8c987]">{poundsFromPence(preview.diff_pence)}</b> more. How would you like to handle the difference?</p>
              <div className="space-y-2">
                <button className="st-btn w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("payment_link")} data-testid="move-payment-link"><Link2 size={14} /> Generate payment link</button>
                <button className="st-btn st-btn-ghost w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("absorb")} data-testid="move-absorb">Absorb the difference</button>
              </div>
            </>
          )}
          {preview.scenario === "decrease" && (
            <>
              <p className="text-sm mb-3">The new date is <b className="text-[#86efac]">{poundsFromPence(preview.diff_pence)}</b> cheaper. Refund the difference?</p>
              <div className="space-y-2">
                <button className="st-btn w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("refund")} data-testid="move-refund">Refund {poundsFromPence(preview.diff_pence)} &amp; move</button>
                <button className="st-btn st-btn-ghost w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("none")} data-testid="move-no-refund">Move without refund</button>
              </div>
            </>
          )}
        </div>
      )}
      {err && <div className="text-sm text-[#fca5a5] mt-3" data-testid="move-error">{err}</div>}
    </ModalShell>
  );
}

function MoveTicketsModal({ orderId, order, attendees, eventData, attraction, onClose, onDone }) {
  const slotted = !!eventData.event?.is_time_slotted;
  const dates = (eventData.dates || []).filter((d) => !d.is_blocked && d.status !== "cancelled").sort((a, b) => a.date.localeCompare(b.date));
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState({});
  const [notifyEmail, setNotifyEmail] = useState(order.customer_email || "");
  const [destDate, setDestDate] = useState(null);
  const [destSlot, setDestSlot] = useState(null);
  const [perf, setPerf] = useState(null);
  const [preview, setPreview] = useState(null);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const origin = window.location.origin;
  const ids = attendees.filter((a) => selected[a.id]).map((a) => a.id);

  const pickDate = async (d) => {
    setDestDate(d); setDestSlot(null); setErr("");
    if (slotted) {
      setPerf(null); setStep(3);
      try { const r = await api.get(`/events/${order.event_id}/dates/${d.id}/performance`); setPerf(r.data); }
      catch { /* noop */ }
    } else {
      await runPreview(d, null);
    }
  };
  const pickSlot = async (s) => { setDestSlot(s); await runPreview(destDate, s); };

  const runPreview = async (d, s) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/orders/${orderId}/move-tickets`, { attendee_ids: ids, event_date_id: d.id, time_slot_id: s?.id || null });
      setPreview(r.data); setStep(4);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Could not check availability"); }
    finally { setBusy(false); }
  };

  const submit = async (price_action) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/orders/${orderId}/move-tickets`, {
        attendee_ids: ids, event_date_id: destDate.id, time_slot_id: destSlot?.id || null,
        price_action, override: override || preview.sufficient, origin,
        notify_email: notifyEmail.trim() || null,
      });
      if (r.data.requires_payment) onDone("Move pending — payment link sent for the difference");
      else onDone(r.data.refunded > 0 ? `${r.data.moved} ticket(s) moved · ${poundsFromPence(r.data.refunded)} refunded` : `${r.data.moved} ticket(s) moved`);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Move failed"); setBusy(false); }
  };

  return (
    <ModalShell title="Move individual tickets" onClose={onClose} testid="move-tickets-modal" maxW="max-w-lg">
      {step === 1 && (
        <div data-testid="move-tickets-step-select">
          <p className="text-sm text-[#bbb] mb-3">
            Choose which ticket(s) to move to another night. The booking and its payment stay on the original order —
            only the selected ticket(s) change date.
          </p>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {attendees.map((a) => (
              <label key={a.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-[#1a1a1a] hover:bg-[#0e0e0e] cursor-pointer" data-testid={`move-ticket-att-${a.id}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <input type="checkbox" checked={!!selected[a.id]} onChange={(e) => setSelected({ ...selected, [a.id]: e.target.checked })} />
                  <span className="truncate">{a.name} <span className="text-xs text-[#8a8a8a]">· {a.ticket_type_name}</span></span>
                </span>
                {a.event_date && <span className="text-xs text-[#8a8a8a] shrink-0">{fmtShort(a.event_date)}</span>}
              </label>
            ))}
          </div>
          <label className="block mt-4">
            <span className="text-xs text-[#8a8a8a]">Email the new ticket to</span>
            <input className="st-input mt-1" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                   placeholder="guest@example.com" data-testid="move-tickets-email" />
            <span className="text-[11px] text-[#6a6a6a]">They'll get a link to <b>their ticket only</b> — not the whole order.</span>
          </label>
          <button className="st-btn w-full justify-center mt-4" disabled={ids.length === 0} onClick={() => setStep(2)} data-testid="move-tickets-next">
            Continue with {ids.length} ticket{ids.length === 1 ? "" : "s"}
          </button>
        </div>
      )}

      {step === 2 && (
        <div data-testid="move-tickets-step-date">
          <button className="text-xs text-[#8a8a8a] underline mb-3" onClick={() => setStep(1)}>← Back to tickets</button>
          <p className="text-sm text-[#bbb] mb-3">Select the new date for {ids.length} ticket{ids.length === 1 ? "" : "s"}.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {dates.map((d) => (
              <button key={d.id} onClick={() => pickDate(d)} disabled={d.id === order.event_date_id && !slotted}
                className={`p-3 rounded-lg border text-sm text-left ${d.id === order.event_date_id ? "border-[#dc2626] bg-[#160a0a]" : "border-[#222] hover:border-[#444]"}`}
                data-testid={`move-tickets-date-${d.id}`}>
                <div className="font-medium">{fmtDate(d.date)}</div>
                {d.id === order.event_date_id && <div className="text-[10px] uppercase tracking-wider text-[#dc2626] mt-0.5">Current</div>}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div data-testid="move-tickets-step-slot">
          <button className="text-xs text-[#8a8a8a] underline mb-3" onClick={() => setStep(2)}>← Back to dates</button>
          <p className="text-sm text-[#bbb] mb-3">Select a time slot on {fmtDate(destDate.date)}.</p>
          {!perf ? <div className="text-sm text-[#8a8a8a]">Loading slots…</div> : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {perf.time_slots.filter((s) => !s.is_paused).map((s) => {
                const fit = slotFitFor(s, demandByType(attendees.filter((a) => selected[a.id])));
                return (
                <button key={s.id} onClick={() => pickSlot(s)}
                  className="w-full flex items-center justify-between p-2.5 rounded-lg border text-sm border-[#222] hover:border-[#444]"
                  data-testid={`move-tickets-slot-${s.id}`}>
                  <span>{formatSlotTime(s.start_time, s.end_time, attraction)}</span>
                  <span className={`text-xs ${fit.ok ? "text-[#8a8a8a]" : "text-[#e8c987]"}`} data-testid={`move-tickets-slot-avail-${s.id}`}>{fit.label}</span>
                </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {step === 4 && preview && (
        <div data-testid="move-tickets-step-review">
          <button className="text-xs text-[#8a8a8a] underline mb-3" onClick={() => setStep(slotted ? 3 : 2)}>← Back</button>
          <div className="st-card bg-[#0e0e0e] p-3 text-sm mb-3">
            <div className="flex justify-between"><span className="text-[#8a8a8a]">Tickets moving</span><span>{preview.ticket_count}</span></div>
            <div className="flex justify-between mt-1"><span className="text-[#8a8a8a]">New date</span><span>{fmtDate(destDate.date)}</span></div>
            {destSlot && <div className="flex justify-between mt-1"><span className="text-[#8a8a8a]">New slot</span><span>{formatSlotTime(destSlot.start_time, destSlot.end_time, attraction)}</span></div>}
            <div className="flex justify-between mt-1"><span className="text-[#8a8a8a]">Rest of order</span><span>Unchanged</span></div>
          </div>

          {!preview.sufficient && (
            <div className="rounded-lg border border-[#3a2a0a] bg-[#1a1205] p-3 text-sm text-[#e8c987] mb-3" data-testid="move-tickets-capacity-warn">
              <ShortfallNote preview={preview} />
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="move-tickets-override" />
                <span>Override and move anyway</span>
              </label>
            </div>
          )}

          {preview.scenario === "same" && (
            <>
              <p className="text-sm mb-3">No price difference. Move {preview.ticket_count} ticket(s)?</p>
              <button className="st-btn w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("none")} data-testid="move-tickets-confirm-same">{busy ? "Moving…" : "Move ticket(s)"}</button>
            </>
          )}
          {preview.scenario === "increase" && (
            <>
              <p className="text-sm mb-3">The new date costs <b className="text-[#e8c987]">{poundsFromPence(preview.diff_pence)}</b> more for these ticket(s). How would you like to handle the difference?</p>
              <div className="space-y-2">
                <button className="st-btn w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("payment_link")} data-testid="move-tickets-payment-link"><Link2 size={14} /> Generate payment link</button>
                <button className="st-btn st-btn-ghost w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("absorb")} data-testid="move-tickets-absorb">Absorb the difference</button>
              </div>
            </>
          )}
          {preview.scenario === "decrease" && (
            <>
              <p className="text-sm mb-3">The new date is <b className="text-[#86efac]">{poundsFromPence(preview.diff_pence)}</b> cheaper. Refund the difference?</p>
              <div className="space-y-2">
                <button className="st-btn w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("refund")} data-testid="move-tickets-refund">Refund {poundsFromPence(preview.diff_pence)} &amp; move</button>
                <button className="st-btn st-btn-ghost w-full justify-center" disabled={busy || (!preview.sufficient && !override)} onClick={() => submit("none")} data-testid="move-tickets-no-refund">Move without refund</button>
              </div>
            </>
          )}
        </div>
      )}
      {err && <div className="text-sm text-[#fca5a5] mt-3" data-testid="move-tickets-error">{err}</div>}
    </ModalShell>
  );
}

function UpgradeTicketsModal({ orderId, order, attendees, eventData, onClose, onDone }) {
  const types = (eventData.ticket_types || []).filter((t) => t.is_active);
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState({});
  const [notifyEmail, setNotifyEmail] = useState(order.customer_email || "");
  const [target, setTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ids = attendees.filter((a) => selected[a.id]).map((a) => a.id);
  const allOn = ids.length === attendees.length && attendees.length > 0;

  const pickType = async (t) => {
    setTarget(t); setBusy(true); setErr("");
    try {
      const r = await api.post(`/orders/${orderId}/upgrade-tickets`, { attendee_ids: ids, ticket_type_id: t.id });
      setPreview(r.data); setStep(3);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Could not price that upgrade"); }
    finally { setBusy(false); }
  };

  const submit = async (price_action) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/orders/${orderId}/upgrade-tickets`, {
        attendee_ids: ids, ticket_type_id: target.id, price_action,
        override: override || preview.sufficient, origin: window.location.origin,
        notify_email: notifyEmail.trim() || null,
      });
      if (r.data.requires_payment) onDone("Upgrade pending — payment link sent for the difference");
      else onDone(r.data.refunded > 0
        ? `${r.data.upgraded} ticket(s) now ${r.data.ticket_type_name} · ${poundsFromPence(r.data.refunded)} refunded`
        : `${r.data.upgraded} ticket(s) now ${r.data.ticket_type_name}`);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Upgrade failed"); setBusy(false); }
  };

  const disabledConfirm = busy || (!preview?.sufficient && !override);

  return (
    <ModalShell title="Upgrade ticket type" onClose={onClose} testid="upgrade-modal" maxW="max-w-lg">
      {step === 1 && (
        <div data-testid="upgrade-step-select">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-[#bbb]">Which tickets are changing type?</p>
            <button className="text-xs st-link" data-testid="upgrade-select-all"
              onClick={() => setSelected(allOn ? {} : Object.fromEntries(attendees.map((a) => [a.id, true])))}>
              {allOn ? "Clear all" : "Select all"}
            </button>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {attendees.map((a) => (
              <label key={a.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-[#1a1a1a] hover:bg-[#0e0e0e] cursor-pointer" data-testid={`upgrade-att-${a.id}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <input type="checkbox" checked={!!selected[a.id]} onChange={(e) => setSelected({ ...selected, [a.id]: e.target.checked })} />
                  <span className="truncate">{a.name}</span>
                </span>
                <span className="text-xs text-[#8a8a8a] shrink-0">{a.ticket_type_name}</span>
              </label>
            ))}
          </div>
          <label className="block mt-4">
            <span className="text-xs text-[#8a8a8a]">Email the new ticket to</span>
            <input className="st-input mt-1" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} data-testid="upgrade-email" />
          </label>
          <button className="st-btn w-full justify-center mt-4" disabled={ids.length === 0} onClick={() => setStep(2)} data-testid="upgrade-next">
            Continue with {ids.length} ticket{ids.length === 1 ? "" : "s"}
          </button>
        </div>
      )}

      {step === 2 && (
        <div data-testid="upgrade-step-type">
          <button className="text-xs text-[#8a8a8a] underline mb-3" onClick={() => setStep(1)}>← Back to tickets</button>
          <p className="text-sm text-[#bbb] mb-3">Change {ids.length} ticket{ids.length === 1 ? "" : "s"} to:</p>
          <div className="space-y-2">
            {types.map((t) => (
              <button key={t.id} onClick={() => pickType(t)} disabled={busy}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-[#222] hover:border-[#dc2626] text-sm text-left"
                data-testid={`upgrade-type-${t.id}`}>
                <span>{t.name}</span>
                <span className="text-[#8a8a8a]">{poundsFromPence(t.base_price_pence)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 3 && preview && (
        <div data-testid="upgrade-step-review">
          <button className="text-xs text-[#8a8a8a] underline mb-3" onClick={() => setStep(2)}>← Back</button>
          <div className="st-card bg-[#0e0e0e] p-3 text-sm mb-3">
            <div className="flex justify-between"><span className="text-[#8a8a8a]">Tickets</span><span>{preview.ticket_count}</span></div>
            <div className="flex justify-between mt-1"><span className="text-[#8a8a8a]">New type</span><span>{preview.ticket_type_name}</span></div>
            <div className="flex justify-between mt-1"><span className="text-[#8a8a8a]">Date &amp; time</span><span>Unchanged</span></div>
          </div>

          {!preview.sufficient && (
            <div className="rounded-lg border border-[#3a2a0a] bg-[#1a1205] p-3 text-sm text-[#e8c987] mb-3" data-testid="upgrade-capacity-warn">
              {preview.shortfalls?.[0]?.disabled
                ? `${preview.ticket_type_name} is switched off for one of these time slots.`
                : `Only ${preview.shortfalls?.[0]?.available ?? 0} ${preview.ticket_type_name} left on that time slot.`}
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="upgrade-override" />
                <span>Override and upgrade anyway</span>
              </label>
            </div>
          )}

          {preview.scenario === "same" && (
            <>
              <p className="text-sm mb-3">No price difference. Change {preview.ticket_count} ticket(s) to {preview.ticket_type_name}?</p>
              <button className="st-btn w-full justify-center" disabled={disabledConfirm} onClick={() => submit("none")} data-testid="upgrade-confirm-same">{busy ? "Upgrading…" : "Confirm upgrade"}</button>
            </>
          )}
          {preview.scenario === "increase" && (
            <>
              <p className="text-sm mb-3">This upgrade costs <b className="text-[#e8c987]">{poundsFromPence(preview.diff_pence)}</b> more. The ticket stays as it is until the money lands.</p>
              <div className="space-y-2">
                <button className="st-btn w-full justify-center" disabled={disabledConfirm} onClick={() => submit("payment_link")} data-testid="upgrade-payment-link"><Link2 size={14} /> Send payment link</button>
                <button className="st-btn st-btn-ghost w-full justify-center" disabled={disabledConfirm} onClick={() => submit("absorb")} data-testid="upgrade-absorb">Upgrade now &amp; absorb the difference</button>
              </div>
            </>
          )}
          {preview.scenario === "decrease" && (
            <>
              <p className="text-sm mb-3">This change is <b className="text-[#86efac]">{poundsFromPence(preview.diff_pence)}</b> cheaper. Refund the difference?</p>
              <div className="space-y-2">
                <button className="st-btn w-full justify-center" disabled={disabledConfirm} onClick={() => submit("refund")} data-testid="upgrade-refund">Refund {poundsFromPence(preview.diff_pence)} &amp; change</button>
                <button className="st-btn st-btn-ghost w-full justify-center" disabled={disabledConfirm} onClick={() => submit("none")} data-testid="upgrade-no-refund">Change without refund</button>
              </div>
            </>
          )}
        </div>
      )}
      {err && <div className="text-sm text-[#fca5a5] mt-3" data-testid="upgrade-error">{err}</div>}
    </ModalShell>
  );
}

function AddTicketsModal({ orderId, order, eventData, onClose, onDone }) {
  const types = (eventData.ticket_types || []).filter((t) => t.is_active);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [override, setOverride] = useState(false);
  const [needOverride, setNeedOverride] = useState(false);
  const origin = window.location.origin;

  const adjust = (id, delta) => setQty((p) => {
    const v = Math.max(0, (Number(p[id]) || 0) + delta);
    const out = { ...p, [id]: v }; if (!v) delete out[id]; return out;
  });
  const items = Object.entries(qty).filter(([, q]) => q > 0).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const totalPence = items.reduce((s, i) => {
    const t = types.find((x) => x.id === i.ticket_type_id); return s + (t?.base_price_pence || 0) * i.quantity;
  }, 0);

  const submit = async (payment_action) => {
    if (payment_action === "comp") {
      const ok = await confirmDialog({ title: "Add complimentary tickets?", description: `${totalQty} ticket(s) will be added at no charge and logged in the audit trail.`, confirmLabel: "Add tickets" });
      if (!ok) return;
    }
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/orders/${orderId}/add-tickets`, { items, payment_action, override, origin });
      if (r.data.requires_payment) onDone("Payment link sent to customer");
      else onDone(`${r.data.added} ticket(s) added`);
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail) || "Add failed";
      setErr(msg);
      if (/spaces remaining/i.test(msg)) setNeedOverride(true);
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add tickets to order" onClose={onClose} testid="add-tickets-modal">
      <p className="text-sm text-[#bbb] mb-3">Tickets are added to the same date &amp; time slot as this order.</p>
      <div className="space-y-2">
        {types.map((t) => (
          <div key={t.id} className="flex items-center justify-between p-2.5 rounded-lg border border-[#1a1a1a]" data-testid={`add-tt-${t.id}`}>
            <div><div className="font-medium text-sm">{t.name}</div><div className="text-xs text-[#8a8a8a]">{poundsFromPence(t.base_price_pence)}</div></div>
            <Counter value={Number(qty[t.id] || 0)} onMinus={() => adjust(t.id, -1)} onPlus={() => adjust(t.id, 1)} testid={`add-counter-${t.id}`} />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-4 text-sm"><span>Total due</span><span className="font-bold" data-testid="add-total">{poundsFromPence(totalPence)}</span></div>

      {needOverride && (
        <label className="flex items-center gap-2 mt-3 text-sm text-[#e8c987] cursor-pointer" data-testid="add-override-wrap">
          <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="add-override" />
          <span>Override capacity and continue</span>
        </label>
      )}
      {err && <div className="text-sm text-[#fca5a5] mt-3" data-testid="add-error">{err}</div>}

      <div className="space-y-2 mt-5">
        <button className="st-btn w-full justify-center" disabled={busy || totalQty === 0} onClick={() => submit("payment_link")} data-testid="add-payment-link"><Link2 size={14} /> Generate payment link</button>
        <button className="st-btn st-btn-ghost w-full justify-center" disabled={busy || totalQty === 0} onClick={() => submit("comp")} data-testid="add-comp">Add without payment (complimentary)</button>
      </div>
    </ModalShell>
  );
}
